import { useEffect, useMemo, useState } from "react";
import Modal from "@mui/material/Modal";
import SupportIcon from "../../../Assets/Icons/SupportIcon";
import { TextField, Box } from "@mui/material";
import AgricultureIcon from "@mui/icons-material/Agriculture";
import Autocomplete from "@mui/material/Autocomplete";
import Select from "@mui/material/Select";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";

import MachineTemplates from "../../../Assets/PlanterObjs.js";
import { useReferenceData } from "../../../hooks/useReferenceData";
import { buildSparsePayload, toOptionalNumber } from "../../../utils/sparsePayload";
import { hasComparableFormChanges } from "../../../utils/formComparison";
import MainButton from "../../Buttons/MainButton";
import ModalShell, { ModalTitle } from "../../ModalShell";

function NewMachinePlanterModal({
  open,
  handleClose,
  handleAddNewMachine,
  machineLabel,
  machineType = "TRACTOR",
  isGeneric = false,
}) {
  const { referenceData } = useReferenceData(["machine_types"]);
  const [newMachineName, setNewMachineName] = useState("");
  const [newMachineHorsepower, setNewMachineHorsepower] = useState(0);
  const [newMachineDefaultAcreHour, setNewMachineDefaultAcreHour] = useState(0);
  const [MachineType, setMachineType] = useState(machineType);

  const formattedMachineType =
    MachineType.slice(0, 1) + MachineType.slice(1).toLowerCase();

  useEffect(() => {
    if (!open) {
      return;
    }

    setNewMachineName("");
    setNewMachineHorsepower(0);
    setNewMachineDefaultAcreHour(0);
    setMachineType(machineType);
  }, [machineType, open]);

  const isSaveDisabled = useMemo(() => {
    const currentPayload = buildSparsePayload({
      type: MachineType,
      name: newMachineName,
      horsepower: toOptionalNumber(newMachineHorsepower),
      default_acre_hour: toOptionalNumber(newMachineDefaultAcreHour),
    });
    const baselinePayload = buildSparsePayload({
      type: machineType,
      name: "",
      horsepower: 0,
      default_acre_hour: 0,
    });

    return !hasComparableFormChanges(currentPayload, baselinePayload);
  }, [
    MachineType,
    machineType,
    newMachineDefaultAcreHour,
    newMachineHorsepower,
    newMachineName,
  ]);

  function AutocompleteSelect() {
    return (
      <Autocomplete
        options={MachineTemplates[MachineType]}
        autoHighlight
        onInputChange={(event, newInputValue) => {
          if (
            Math.min(
              newInputValue.indexOf("HP"),
              newInputValue.indexOf("AcreHour")
            ) < 0
          ) {
            return;
          }
          let parsedData = parsePlanterInfo(newInputValue);
          setNewMachineName(parsedData.name);
          setNewMachineHorsepower(parsedData.horsepower);
          setNewMachineDefaultAcreHour(parsedData.default_acre_hour);
        }}
        getOptionLabel={(option) =>
          option.brand +
          " " +
          option.model +
          "  " +
          `HP: ${option.HP}, AcreHour: ${option.acreHour}`
        }
        renderOption={(props, option) => {
          const { key, ...optionProps } = props;
          return (
            <Box
              key={key}
              component="li"
              sx={{ "& > img": { mr: 2, flexShrink: 0 } }}
              {...optionProps}
            >
              {option.brand} - {option.model}
            </Box>
          );
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label={`Choose a ${formattedMachineType} Preset From Template`}
            inputProps={{
              ...params.inputProps,
            }}
            variant="standard"
          />
        )}
      />
    );
  }

  return (
    <Modal
      open={open}
      onClose={() => handleClose(false)}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <ModalShell
        accent="info"
        icon={<SupportIcon color={"#FFFFFF"} width="38px" height="38px" />}
        onClose={() => handleClose(false)}
        width="min(620px, calc(100vw - 32px))"
      >
          <ModalTitle accent="info" className="text-[20px]">
            Add a new {machineLabel} to your farm <AgricultureIcon />
          </ModalTitle>

          <div className="flex flex-col items-center justify-center">
            {isGeneric && (
              <FormControl
                variant="outlined"
                sx={{ marginBottom: "8px", width: "100%" }}
              >
                <InputLabel id="demo-simple-select-standard-label">
                  Equipment Type
                </InputLabel>
                <Select
                  labelId="demo-simple-select-standard-label"
                  id="demo-simple-select-standard"
                  value={MachineType}
                  onChange={(e) => setMachineType(e.target.value)}
                  label="Equipment Type"
                >
                  {(referenceData.machine_types || []).map((machineTypeOption) => (
                    <MenuItem
                      key={machineTypeOption.value}
                      value={machineTypeOption.value}
                    >
                      {machineTypeOption.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            <div className="mb-4 w-full">
              <AutocompleteSelect />
            </div>
            <TextField
              id="standard-basic"
              label={`${machineLabel} Name`}
              variant="outlined"
              sx={{ marginBottom: 2, width: "100%" }}
              onChange={(e) => setNewMachineName(e.target.value)}
              value={newMachineName}
            />
            <TextField
              id="standard-basic"
              label="Horse Power (HP)"
              sx={{ marginBottom: 2, width: "100%" }}
              type="number"
              InputProps={{ inputProps: { min: 0 } }}
              variant="outlined"
              onChange={(e) => setNewMachineHorsepower(e.target.value)}
              value={newMachineHorsepower}
            />
            <TextField
              id="standard-basic"
              label="Default ac/hr"
              sx={{ marginBottom: 2, width: "100%" }}
              type="number"
              InputProps={{ inputProps: { min: 0 } }}
              variant="outlined"
              onChange={(e) => setNewMachineDefaultAcreHour(e.target.value)}
              value={newMachineDefaultAcreHour}
            />
          </div>
          <div className="mt-6 flex w-full justify-center gap-4">
            <MainButton
              onClick={() => {
                handleClose(false);
                handleAddNewMachine({
                  type: MachineType,
                  name: newMachineName,
                  horsepower: toOptionalNumber(newMachineHorsepower),
                  default_acre_hour: toOptionalNumber(newMachineDefaultAcreHour),
                });
              }}
              text="Save"
              disabled={isSaveDisabled}
            />
            <MainButton
              onClick={() => handleClose(false)}
              secondary={true}
              text="Cancel"
            />
          </div>
      </ModalShell>
    </Modal>
  );
}

export default NewMachinePlanterModal;

function parsePlanterInfo(input) {
  // Regular expression to match the name, HP, and AcreHour
  const nameRegex = /^(.+?)\s*HP:/;
  const hpRegex = /HP:\s*(\d+)/;
  const acreHourRegex = /AcreHour:\s*(\d+)/;

  // Extracting the name using the regular expression
  const nameMatch = input.match(nameRegex);
  const hpMatch = input.match(hpRegex);
  const acreHourMatch = input.match(acreHourRegex);

  // Returning an object with the parsed name, HP, and acreHour
  return {
    name: nameMatch ? nameMatch[1].trim() : null,
    horsepower: hpMatch ? parseInt(hpMatch[1], 10) : null,
    default_acre_hour: acreHourMatch ? parseInt(acreHourMatch[1], 10) : null,
  };
}
